import { GlobalStateController } from "bai-react-global-state";
import React from "react";

import { presentations as presentationsApi } from "apis/callables";
import { AuthoringBlockType, CommentFilters, QuickStartTask } from "common/constants";
import { ActionPermissionsObject } from "common/interfaces";
import { computeChangeSet } from "common/utils/changeset";
import { withMaxConcurrency } from "common/utils/withMaxConcurrency";
import Analytics from "js/analytics";
import { getCanvasBundle } from "js/canvas";
import { appVersion } from "js/config";
import CommentsController from "js/controllers/CommentsController";
import QuickStartController from "js/controllers/QuickStartController";
import WorkspaceController, { getPresentationsUsingSharedThemeCount, getWorkspaceResource } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { CollaborationSlidesLockService } from "js/core/services/collaborationSlidesLock";
import themeManager from "js/core/themeManager";
import * as geom from "js/core/utilities/geom";
import { htmlToText, sanitizeHtmlText } from "js/core/utilities/htmlTextHelpers";
import { pluralize, trackActivity } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { app } from "js/namespaces";
import ux from "js/core/services/userExperiences";
import {
    ShowConfirmationDialog,
    ShowDialogAsync,
    ShowErrorDialog,
    ShowInputDialog,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";
import { _ } from "js/vendor";

const logger = getLogger(LogGroup.EDITOR);

export const PanelType = {
    NOTES: "notes",
    COMMENTS: "comments",
    ASSIGN_SLIDE: "assign-slide",
    VERSION_HISTORY: "version-history",
    ELEMENT: "element",
    ANIMATION: "animation",
    RECORD: "record",
    SHORTCUTS: "shortcuts",
    ADVANCE_ON: "advance-on",
    MORE_ACTIONS: "more-actions",
    SLIDE_AI: "slide-ai"
};

const PRE_RENDER_SLIDES_RANGE = 1;

export const SLIDE_NAVIGATOR_TRANSITION_DURATION = 500;

const initialState = {
    isInitialized: false,
    currentSlide: null,
    presentation: null,
    activeRightPanel: null,
    activeBottomPanel: null,
    showCanvasControls: true,
    showSelectionLayer: true,
    isSingleSlideEditor: false,
    currentCanvasController: null,
    currentSelectionLayerController: null,
    showSlideGrid: false,
    canvasControllers: {},
    selectionLayerControllers: {},
    currentSlideLockOwner: null,
    isCurrentSlideLockedForMe: false,
    isCurrentSlideLockedByMe: false,
    allowThemeChange: true,
    allowEditing: true,
    popupOpenState: false,
    hidePanels: [],
    slides: [],
    currentTheme: null,
    freezePropertiesPanel: false,
    showAddElementDropZone: false,
    ElementPanel: null,
    elementPanelElement: null,
    selectedPropertyPanelTab: null,
    selectedPropertyPanelTabProps: {},
    showPropertyPanel: false,
    rightPanelTransitionState: null,
    showSlideNav: true,
    slideNavSelected: false,
    handleKeyEvents: true,
    showFindAndReplaceBar: false,
    slideNavWidth: 285,
    userSlideNavWidth: 285,
    slideNavMode: "nav",
    presentationResource: null,
    // Selections made inside the comments panel. They live here (instead of in the panel's
    // component state) because the property panel subtree is remounted on every slide
    // navigation, which would otherwise reset them
    commentsPaneSelections: {
        byViewer: false,
        commentFilter: CommentFilters.CurrentSlide
    },
};

class PresentationEditorController extends GlobalStateController {
    constructor(initialState) {
        super(initialState);

        this.asyncActionsPromiseChain = Promise.resolve();
        this.collaborationSlidesLockService = null;
        this.collaborationSlidesLockServiceSubscription = null;
        this.unfreezePropertiesPanelTimeout = null;
        this.pushStateTimeout = null;
        this.deferredSlidesRenderTimeout = null;
        this.ignorePresentationThemeChanges = false;
        this.preloadedPresentation = null;
    }

    get presentation() {
        return this._state.presentation;
    }

    get slides() {
        return this._state.slides;
    }

    setPresentation = presentation => {
        return this._updateState({ presentation });
    }

    setCurrentTheme = theme => {
        return this._updateState({ currentTheme: theme });
    }

    get currentSlide() {
        return this._state.currentSlide;
    }

    get isInitialized() {
        return this._state.isInitialized;
    }

    get isSingleSlideEditor() {
        return this._state.isSingleSlideEditor;
    }

    get activeRightPanel() {
        return this._state.activeRightPanel;
    }

    get activeBottomPanel() {
        return this._state.activeBottomPanel;
    }

    get elementPanelElement() {
        return this._state.elementPanelElement;
    }

    get currentTheme() {
        return this._state.currentTheme;
    }

    get currentCanvasController() {
        return this._state.currentCanvasController;
    }

    get experiments() {
        return this._state.experiments;
    }

    get uiPrefs() {
        return this._state.uiPrefs;
    }

    get presentationResource() {
        return this._state.presentationResource;
    }

    setUIPrefs(pref) {
        this._updateState({ uiPrefs: { ...this.uiPrefs, ...pref } });
        localStorage.setItem("uiPrefs", JSON.stringify(this.uiPrefs));
    }

    _stateDidUpdate(prevState) {
        const { currentSlideLockOwner, currentSlide, slides, isInitialized, slideNavMode } = this._state;

        if (isInitialized && prevState.currentSlideLockOwner !== currentSlideLockOwner) {
            if (currentSlideLockOwner) {
                this.hideSelectionLayer();
            } else {
                this.showSelectionLayer();
            }
        }

        if (isInitialized && prevState.slideNavMode !== slideNavMode) {
            if (slideNavMode === "nav") {
                setTimeout(() => {
                    this.showSelectionLayer();
                }, SLIDE_NAVIGATOR_TRANSITION_DURATION);
            } else {
                this.hideSelectionLayer();
            }
        }

        if (currentSlide && prevState.currentSlide !== currentSlide) {
            if (!app.isEditingText) {
                this.renderCurrentAndSurroundingSlides();
            } else {
                this._scheduleRenderAfterTextEdit();
            }
        } else if (!_.isEqual(slides.map(s => s?.id), prevState.slides.map(s => s?.id)) && currentSlide) {
            if (!app.isEditingText) {
                this.renderCurrentAndSurroundingSlides();
            } else {
                this._scheduleRenderAfterTextEdit();
            }
        }
    }

    _scheduleRenderAfterTextEdit() {
        if (this.deferredSlidesRenderTimeout) {
            return;
        }

        const attemptRender = () => {
            if (app.isEditingText) {
                this.deferredSlidesRenderTimeout = setTimeout(attemptRender, 300);
                return;
            }

            this.deferredSlidesRenderTimeout = null;
            if (this._state.currentSlide) {
                // Render current slide now that editing has finished
                const canvasController = this._state.canvasControllers[this._state.currentSlide.id];
                if (canvasController) {
                    canvasController.renderCanvas();
                }
            }
        };

        this.deferredSlidesRenderTimeout = setTimeout(attemptRender, 300);
    }

    _stateWillUpdate(nextState) {
        const { currentSlideLockOwner } = nextState;
        nextState.isCurrentSlideLockedForMe = !!currentSlideLockOwner;
        return nextState;
    }

    async renderCurrentAndSurroundingSlides() {
        const { slides, canvasControllers, presentation } = this._state;

        if (!presentation) {
            return;
        }

        const currentSlideIndex = this.getCurrentSlideIndex();

        // Giving priority to the closest slides to the current one, i.e. current->next->prev->next+1->prev-1->next+2->prev-2->...
        const offsets = [0];
        new Array(PRE_RENDER_SLIDES_RANGE).fill(0).forEach((_, index) => {
            offsets.push(index + 1);
            offsets.push(-index - 1);
        });
        const slideIndexes = offsets.map(offset => currentSlideIndex + offset).filter(slideIndex => slideIndex >= 0 && slideIndex < slides.length);

        const renderSlide = async slide => {
            const canvasController = canvasControllers[slide.id];
            if (canvasController) {
                await canvasController.renderCanvas()
                    .catch(err => logger.error(err, "[PresentationEditorController] renderCurrentAndSurroundingSlides() error rendering slide", { slideId: slide.id }));
            }
        };

        const currentSlide = slides[currentSlideIndex];
        const otherSlides = slideIndexes.filter(slideIndex => slideIndex !== currentSlideIndex).map(slideIndex => slides[slideIndex]);

        // First render the current slide (skip if actively editing text to prevent losing edits)
        if (!app.isEditingText) {
            await renderSlide(currentSlide);
        }

        // Give some time for UI transitions
        await new Promise(resolve => setTimeout(resolve, 500));

        // Then render the other slides
        for (const slide of otherSlides) {
            if (!this._state.presentation) {
                // Editor was reset, stop rendering
                return;
            }

            if (currentSlideIndex !== this.getCurrentSlideIndex()) {
                // Current slide has changed, stop rendering (the new renderCurrentAndSurroundingSlides() call will take over)
                return;
            }

            await renderSlide(slide);
        }
    }

    /**
     * Wraps the component with PresentationEditorController state, current CanvasController and current SelectionLayerController states.
     * Use only when 100% have to, this is a performance killer if not used wisely.
     */
    withFullState(Component, shouldUpdate = (props, nextProps) => true) {
        class ComponentWithFullState extends React.Component {
            constructor(props) {
                super(props);

                this.wrappedComponents = {};
            }

            shouldComponentUpdate(nextProps) {
                return shouldUpdate(this.props, nextProps);
            }

            render() {
                const { currentCanvasController, currentSelectionLayerController, innerRef } = this.props;

                if (!currentCanvasController || !currentSelectionLayerController) {
                    return null;
                }

                const wrapperId = `${currentCanvasController.instanceId}-${currentSelectionLayerController.instanceId}`;

                // Creating new wrapper during render so we don't have to cause additional re-render
                if (!this.wrappedComponents[wrapperId]) {
                    this.wrappedComponents[wrapperId] =
                        currentCanvasController.withState(
                            currentSelectionLayerController.withState(
                                Component
                            )
                        );
                }

                const WrappedComponent = this.wrappedComponents[wrapperId];
                return <WrappedComponent {...this.props} ref={innerRef} />;
            }
        }

        return this.withState(React.forwardRef((props, ref) => <ComponentWithFullState {...props} innerRef={ref} />));
    }

    getCurrentCanvasController() {
        return this._state.currentCanvasController;
    }

    get canvasController() {
        return this.getCurrentCanvasController();
    }

    getCurrentSelectionLayerController() {
        return this._state.currentSelectionLayerController;
    }

    get selectionLayerController() {
        return this.getCurrentSelectionLayerController();
    }

    get skippedSlides() {
        return this.presentation.get("skippedSlides") ?? {};
    }

    getCanvasControllerForSlide(slide) {
        return this._state.canvasControllers[slide.id];
    }

    getCanvasControllers() {
        return this._state.canvasControllers;
    }

    getPopupOpenState() {
        return this._state.popupOpenState;
    }

    getCanvases() {
        return Object.values(this._state.canvasControllers)
            .filter(c => c.canvas)
            .map(c => c.canvas);
    }

    getCurrentSlideIndex() {
        const { presentation, currentSlide } = this._state;
        return presentation?.getSlideIndex(currentSlide.id);
    }

    // Returns a promise that resolves once all currently-queued async operations complete.
    // Use this to ensure pending slide syncs (e.g. from presentation.reload()) finish before
    // returning control to the user, preventing stale operations from interfering with
    // subsequent user actions like adding a slide.
    flushPendingOperations() {
        return new Promise((resolve, reject) => {
            this.asyncActionsPromiseChain = this.asyncActionsPromiseChain
                .then(resolve)
                .catch(reject);
        });
    }

    canShowSelectionLayer(slide, canvasController, isSingleSlideEditor, slideNavMode) {
        if (!canvasController) {
            return false;
        }

        return (!slide?.isLibrarySlide() || isSingleSlideEditor) && !canvasController.isTemplateObsolete && slideNavMode === "nav";
    }

    setShowAddElementDropZone(showAddElementDropZone) {
        return this._updateState({ showAddElementDropZone });
    }

    setHandleKeyEvents = handleKeyEvents => {
        return this._updateState({ handleKeyEvents });
    }

    get isAiPropertyPanelOpen() {
        return this._state.selectedPropertyPanelTab === PanelType.SLIDE_AI && this._state.showPropertyPanel;
    }

    get isCommentsPanelActive() {
        return this._state.selectedPropertyPanelTab === PanelType.COMMENTS;
    }

    get commentsPaneSelections() {
        return this._state.commentsPaneSelections;
    }

    setCommentsPaneSelections(selections) {
        return this._updateState({
            commentsPaneSelections: { ...this._state.commentsPaneSelections, ...selections }
        });
    }

    async _reset() {
        logger.info("[PresentationEditorController] reset()");

        if (!this._state.isInitialized) {
            return;
        }

        const { presentation, currentCanvasController, currentSelectionLayerController, currentSlide, canvasControllers } = this._state;

        clearTimeout(this.pushStateTimeout);

        if (currentCanvasController) {
            await currentCanvasController.removeAsCurrentCanvas();
        }
        if (currentSelectionLayerController) {
            await currentSelectionLayerController.reset();
        }
        if (presentation) {
            await presentation.setFinishedEditingAt();

            presentation.off("change", this._onPresentationChange);
            presentation.off("slidesUpdated", this._onPresentationSlidesUpdated);
            presentation.off("remoteError", this._onPresentationRemoteError);
        }
        if (currentSlide) {
            currentSlide.off("change", this._onCurrentSlideChange);
        }

        Object.values(canvasControllers).forEach(canvasController => canvasController.dispose());

        if (ds.selection) {
            ds.selection.presentation = null;
        }

        app.undoManager.reset();

        if (this.collaborationSlidesLockServiceSubscription) {
            this.collaborationSlidesLockServiceSubscription.unsubscribe();
        }

        if (this.collaborationSlidesLockService) {
            await this.collaborationSlidesLockService.dispose();
            this.collaborationSlidesLockService = null;
        }

        await CommentsController.reset();

        await QuickStartController.reset();

        await this._updateState(() => _.cloneDeep(initialState));
    }

    async reset(runSequentially = true) {
        // remove listeners for offline status changes
        window.removeEventListener("offline", this.onWindowOffline);
        window.removeEventListener("online", this.onWindowOnline);

        if (!runSequentially) {
            return this._reset();
        }

        return new Promise((resolve, reject) => {
            this.asyncActionsPromiseChain = this.asyncActionsPromiseChain
                .then(() => this._reset())
                .then(resolve)
                .catch(reject);
        });
    }

    async _instantiateCanvasController(presentation, slide) {
        const { CanvasController } = await import(/* webpackMode: "eager" */ "js/editor/PresentationEditor/CanvasController");

        return new CanvasController({
            presentation,
            slide,
            canvasWidth: 1280,
            canvasHeight: 720,
            onCanvasRendered: this.onCanvasRendered
        }, this.collaborationSlidesLockService);
    }

    async _instantiateSelectionLayerController(canvasController) {
        const { SelectionLayerController } = await import(/* webpackMode: "eager" */ "js/editor/PresentationEditor/SelectionLayerController");

        return new SelectionLayerController({ canvasController });
    }

    onWindowOnline = () => this._onOfflineStatusChange(true);
    onWindowOffline = () => this._onOfflineStatusChange(false);

    _onOfflineStatusChange = isOnline => {
        if (isOnline) {
            const presentation = this._state.presentation;

            // reload the presentation to get the latest data when coming back online
            logger.info("[PresentationEditorController] onOfflineStatusChange()", { presentationId: presentation.id });
            presentation.reload()
                .catch(err => {
                    logger.error(err, "[PresentationEditorController] onOfflineStatusChange() error reloading presentation", { presentationId: presentation.id });

                    ShowErrorDialog({
                        title: "Network Error",
                        message: <>
                            There may be a temporary problem with your internet connection and we could not reload the presentation.<br />
                            The page will be reloaded.<br /><br />
                            If the problem persists, please contact <a href="mailto:support@beautiful.ai">support@beautiful.ai</a>
                        </>,
                        // Force reload the page to make sure the broken state doesn't persist
                        onClose: () => window.location.reload()
                    });
                });
        }
    }

    async loadPresentation(presentation, slideIndex = 0, isSingleSlideEditor = false, allowThemeChange = true, allowEditing = true, hidePanels = []) {
        return new Promise((resolve, reject) => {
            this.asyncActionsPromiseChain = this.asyncActionsPromiseChain
                .then(async () => {
                    logger.info("[PresentationEditorController] loadPresentation()", { presentationId: presentation.id, slideIndex, isSingleSlideEditor, allowThemeChange, hidePanels });

                    const presentationResourcePromise = getWorkspaceResource({
                        workspaceId: WorkspaceController.workspaceId,
                        resourceId: presentation.id
                    });

                    // Force reset state
                    await this.reset(false);

                    // listen for offline status changes
                    window.addEventListener("offline", this.onWindowOffline);
                    window.addEventListener("online", this.onWindowOnline);

                    let uiPrefs;
                    try {
                        uiPrefs = JSON.parse(localStorage.getItem("uiPrefs") ?? "{}");
                    } catch {
                        uiPrefs = {};
                    }

                    await this._updateState({
                        isSingleSlideEditor,
                        allowEditing,
                        allowThemeChange,
                        hidePanels,
                        uiPrefs,
                        uiTheme: uiPrefs.theme ?? "dark",
                        showSlideNav: uiPrefs.showSlideNav ?? true,
                        slideNavWidth: uiPrefs.slideNavWidth ?? 280,
                        showPropertyPanel: uiPrefs.showPropertyPanel ?? false,
                        selectedPropertyPanelTab: uiPrefs.showPropertyPanel ? "element" : null
                    });

                    await presentation.load();

                    // Setup collaboration slides lock service if presentation is not dummy and not single slide editor
                    if (!presentation.get("isDummy") === true && !this.isSingleSlideEditor) {
                        this.collaborationSlidesLockService = new CollaborationSlidesLockService(presentation.id);
                        await this.collaborationSlidesLockService.initialize();
                        this.collaborationSlidesLockServiceSubscription = this.collaborationSlidesLockService.observable.subscribe(({ prev, curr }) => {
                            const currentSlideId = this.currentSlide?.id;
                            if (!currentSlideId) {
                                return;
                            }
                            const prevLock = prev[currentSlideId];
                            const currLock = curr[currentSlideId];

                            if (!prevLock?.isLockedForMe && currLock?.isLockedForMe) {
                                this._updateState({ currentSlideLockOwner: currLock.lockedBy });
                            } else if (prevLock?.isLockedForMe && !currLock?.isLockedForMe) {
                                this._updateState({ currentSlideLockOwner: null });
                            }

                            if (!prevLock?.isLockedByMe && currLock?.isLockedByMe) {
                                this._updateState({ isCurrentSlideLockedByMe: true });
                            } else if (prevLock?.isLockedByMe && !currLock?.isLockedByMe) {
                                this._updateState({ isCurrentSlideLockedByMe: false });
                            }
                        });
                    }

                    // Add presentationId to analytics state
                    Analytics.util.trackState({ presentationId: presentation.id });

                    // Add presentationId to recent presentations
                    AppController.user.addPresentationsToRecent([presentation.id]);

                    QuickStartController.initialize().catch(err => {
                        logger.error(err, "[PresentationEditorController] error initializing QuickStartController");
                    });

                    // Immediately start loading slides metadata (if presentation is not dummy)
                    this.loadSlidesMetadata(presentation);

                    ds.selection.presentation = presentation;

                    presentation.loadPresentationLinks();

                    await presentation.prepareSlides(false);

                    const theme = await themeManager.loadTheme(presentation);

                    // create canvas and selection layer controllers for all the slides
                    const canvasControllers = {};
                    const selectionLayerControllers = {};
                    for (const slide of presentation.slides.models) {
                        canvasControllers[slide.id] = await this._instantiateCanvasController(presentation, slide);
                        selectionLayerControllers[slide.id] = await this._instantiateSelectionLayerController(canvasControllers[slide.id]);
                        canvasControllers[slide.id].selectionLayerController = selectionLayerControllers[slide.id];

                        if (presentation.slides.models.indexOf(slide) === slideIndex) {
                            // Rendering current slide (so the editor is ready to be used)
                            const canvasController = canvasControllers[slide.id];
                            try {
                                await canvasController.renderCanvas();
                            } catch (err) {
                                logger.error(err, "[PresentationEditorController] error rendering current slide, will retry..", { slideId: slide.id });
                                await new Promise(resolve => setTimeout(resolve, 100)); // Just in case
                                canvasController.renderCanvasPromise = null;
                                await canvasController.renderCanvas()
                                    .catch(err => {
                                        logger.error(err, "[PresentationEditorController] error rendering current slide, will keep in broken state", { slideId: slide.id });
                                    });
                            }
                        }
                    }

                    await this._updateState({
                        presentationResource: await presentationResourcePromise,
                        presentation,
                        // Keeping references to the slides but busting the reference to the array,
                        // see below for more details
                        slides: [...presentation.slides.models],
                        currentTheme: theme,
                        canvasControllers,
                        selectionLayerControllers
                    });
                    app.currentTheme = theme;

                    await this.setCurrentSlideByIndex(slideIndex, false);

                    presentation.on("change", this._onPresentationChange);
                    presentation.on("slidesUpdated", this._onPresentationSlidesUpdated);

                    const presentationWorkspaceId = presentation.getWorkspaceId();
                    if (presentationWorkspaceId !== AppController.workspaceId) {
                        AppController.switchWorkspace(presentationWorkspaceId, false);
                    }

                    // Fire and forget (if presentation is not dummy)
                    if (!presentation.get("isDummy")) {
                        CommentsController.initialize({
                            presentationId: presentation.id,
                            presentationLinkId: null,
                            byViewer: false,
                            firebaseUser: AppController.firebaseUser
                        }).catch(err => {
                            logger.error(err, "[PresentationEditorController] error initializing comments controller");
                        });
                    }

                    await this._updateState({ isInitialized: true });

                    if (presentation.has("errorLoadingSharedTheme")) {
                        ShowWarningDialog({
                            title: "Missing Theme",
                            message: <>
                                The theme used in this presentation is missing and your slides may look different. <br />
                                Switch to another theme to continue editing.
                            </>,
                            acceptButtonText: "Got It",
                            onClose: () => this.showSwitchThemeDialog()
                        });
                    }
                })
                .then(resolve)
                .catch(reject);
        });
    }

    loadSlidesMetadata(presentation) {
        this.slidesMetadataLoadPromise =
            presentation.get("isDummy") === true
                ? Promise.resolve({})
                : presentationsApi.getSlidesMetadata({ id: presentation.id });
    }

    async reloadPresentation() {
        const { presentation, isInitialized } = this._state;
        if (!isInitialized) {
            return;
        }

        await presentation.reload();

        const presentationWorkspaceId = presentation.getWorkspaceId();
        if (presentationWorkspaceId !== AppController.workspaceId) {
            AppController.switchWorkspace(presentationWorkspaceId, false);
        }
    }

    _onPresentationChange = presentation => {
        (async () => {
            await this.setPresentation(presentation);

            if (this._state.isInitialized && !presentation.isReloading && !this.ignorePresentationThemeChanges && presentation.hasThemeChanged()) {
                await this.updateTheme();
            }
        })().catch(err => {
            logger.error(err, "[PresentationEditorController] _onPresentationChange failed", {
                presentationId: presentation?.id
            });
        });
    }

    _onPresentationSlidesUpdated = (updatedSlides, options) => {
        // If user is actively editing text, defer the entire slides sync to prevent
        // losing in-progress text edits due to canvas re-rendering
        if (app.isEditingText) {
            this._deferSlidesUpdate(updatedSlides, options);
            return;
        }

        // Keeping references to the slides but busting the reference to the array
        // itself so we're mutation safe in cases when slides are added/removed to/from
        // the initial array (e.g. presentation slides)
        this.syncSlidesWithPresentation([...updatedSlides], options);
    }

    _deferSlidesUpdate = (updatedSlides, options) => {
        // Store the latest update to apply when text editing is done
        this._pendingSlidesUpdate = { updatedSlides: [...updatedSlides], options };

        if (this._deferredSlidesUpdateTimeout) {
            return; // Already waiting
        }

        const checkAndSync = () => {
            if (app.isEditingText) {
                // Still editing, check again
                this._deferredSlidesUpdateTimeout = setTimeout(checkAndSync, 100);
            } else {
                // Done editing, apply the pending update
                this._deferredSlidesUpdateTimeout = null;
                if (this._pendingSlidesUpdate) {
                    const { updatedSlides, options } = this._pendingSlidesUpdate;
                    this._pendingSlidesUpdate = null;
                    this.syncSlidesWithPresentation(updatedSlides, options);
                }
            }
        };

        this._deferredSlidesUpdateTimeout = setTimeout(checkAndSync, 100);
    }

    _onPresentationRemoteError = error => {
        logger.error(error, "[PresentationEditorController] onPresentationRemoteError()");

        ShowErrorDialog({
            title: "We were unable to update your presentation.",
            message: <>
                {error.status === 409 && <span>
                    Oops! Another user made changes to this presentation at the same time you did. We’ve loaded the most recent version for you to edit.<br />
                    If the problem persists, please contact <a href="mailto:support@beautiful.ai">support@beautiful.ai</a>
                </span>}
                {error.status !== 409 && <span>
                    There may be a temporary problem with your internet connection.<br />
                    If the problem persists, please contact <a href="mailto:support@beautiful.ai">support@beautiful.ai</a>
                </span>}
            </>,
        });
    }

    async syncSlidesWithPresentation(updatedSlides, { isSlideCreatedFromUserAction } = {}) {
        await new Promise((resolve, reject) => {
            this.asyncActionsPromiseChain = this.asyncActionsPromiseChain
                .then(async () => {
                    const { presentation, slides, canvasControllers, selectionLayerControllers, currentSlide } = this._state;

                    const newSlides = [];

                    // Find deleted slides
                    if (_.intersection(slides, updatedSlides).length === 0) {
                        // All slide instances differ (e.g. after a reload). Try to navigate to the
                        // current slide by ID first; fall back to slide 1 only if truly gone.
                        const currentSlideById = currentSlide && updatedSlides.find(s => s.id === currentSlide.id);
                        await this.setCurrentSlide(currentSlideById ?? updatedSlides[0], false);
                    } else if (!updatedSlides.contains(currentSlide)) {
                        let currentSlide = this._state.currentSlide;
                        let reversed = false;
                        // First go forward to find the next slide that exists, if not found, then go backwards
                        while (!updatedSlides.contains(currentSlide)) {
                            const slideIndex = slides.findIndex(slide => slide.id === currentSlide.id) + (reversed ? -1 : 1);
                            if (slideIndex === slides.length) {
                                reversed = true;
                                continue;
                            }
                            currentSlide = slides[slideIndex];
                        }

                        await this.setCurrentSlide(currentSlide, false);
                    }

                    const newSelectionLayerControllers = {};
                    const newCanvasControllers = {};
                    for (const slide of updatedSlides) {
                        if (canvasControllers[slide.id]) {
                            // Existing slide
                            newCanvasControllers[slide.id] = canvasControllers[slide.id];
                            const canvas = newCanvasControllers[slide.id].canvas;
                            const footer = canvas?.layouter?.elements.footer;
                            // Skip footer refresh on current slide if user is editing text to prevent focus loss
                            const isCurrentSlide = slide.id === currentSlide?.id;
                            if (footer && !canvas.layouter.isGenerating && !(isCurrentSlide && app.isEditingText)) {
                                // Force-refresh footer so the page numbers are updated
                                canvas.refreshElement(footer, false, true, false);
                            }
                            // Existing selection layer
                            newSelectionLayerControllers[slide.id] = selectionLayerControllers[slide.id];
                        } else {
                            // New slide
                            const canvasController = await this._instantiateCanvasController(presentation, slide);
                            newCanvasControllers[slide.id] = canvasController;

                            // New selection layer
                            newSelectionLayerControllers[slide.id] = await this._instantiateSelectionLayerController(canvasController);
                            canvasController.selectionLayerController = newSelectionLayerControllers[slide.id];

                            newSlides.push(slide);
                        }

                        newCanvasControllers[slide.id]._state.selectionLayerController = newSelectionLayerControllers[slide.id];
                    }

                    await this._updateState({
                        slides: updatedSlides,
                        canvasControllers: newCanvasControllers,
                        selectionLayerControllers: newSelectionLayerControllers
                    });

                    if (newSlides.length > 0 && isSlideCreatedFromUserAction) {
                        // If the slide was created from a user action, then we need to
                        // update the current slide to the new slide
                        await this.setCurrentSlide(newSlides[0], false);
                    }

                    // Ensure the surrounding slides are rendered in case
                    // new slides were added near the current slide
                    // NOTE: we're not going to await it
                    // Skip rendering if user is actively editing text to prevent losing edits
                    if (!app.isEditingText) {
                        this.renderCurrentAndSurroundingSlides();
                    } else {
                        this._scheduleRenderAfterTextEdit();
                    }
                })
                .then(resolve)
                .catch(reject);
        });
    }

    getCurrentSlide() {
        return this._state.currentSlide;
    }

    goNextSlide() {
        return this.setCurrentSlideByIndex(this.getCurrentSlideIndex() + 1);
    }

    goPrevSlide() {
        return this.setCurrentSlideByIndex(this.getCurrentSlideIndex() - 1);
    }

    _shouldUpdatePresentationTitle = slide => {
        // We only want this to occur the first time the title block on the title slide changes
        try {
            if (this.getCurrentSlideIndex() === 0 &&
                slide.get("template_id") === "title" &&
                this.presentation.get("name").startsWith("Untitled")) {
                let changeSet = computeChangeSet(slide._previousAttributes, slide.attributes);
                let path = "states[0].primary.text.blocks[0].html";

                if ((changeSet.hasUpdates) && _.get(changeSet.original, path) !== _.get(changeSet.update, path)) {
                    return _.get(changeSet.update, path);
                }
            }
        } catch {
            return null;
        }
        return null;
    }

    _onCurrentSlideChange = slide => {
        const { currentCanvasController, isSingleSlideEditor, showSelectionLayer, slideNavMode } = this._state;

        const stateUpdate = { currentSlide: slide };
        if (slide.changedAttributes()?.libraryItemId && showSelectionLayer) {
            stateUpdate.showSelectionLayer = this.canShowSelectionLayer(slide, currentCanvasController, isSingleSlideEditor, slideNavMode);
        }

        const newPresentationTitle = this._shouldUpdatePresentationTitle(slide);
        if (newPresentationTitle) {
            this.presentation.rename(_.unescape(htmlToText(newPresentationTitle)));
        }

        this._updateState(stateUpdate);
    }

    async setCurrentSlide(slide, runSequentially = true, isTriggeredByPopState) {
        if (!slide) {
            throw new Error(`setCurrentSlide() called with null or undefined slide parameter`);
        }

        clearTimeout(this.unfreezePropertiesPanelTimeout);

        const setCurrentSlide = async () => {
            const { slides, presentation, canvasControllers, selectionLayerControllers, currentSelectionLayerController, currentCanvasController, currentSlide, isSingleSlideEditor, slideNavMode, allowEditing } = this._state;

            if (typeof slide === "string") {
                const slideId = slide;
                slide = slides.find(s => s.id === slideId);
                if (!slide) {
                    throw new Error(`Slide with id ${slideId} not found`);
                }
            }

            if (currentSlide) {
                currentSlide.off("change", this._onCurrentSlideChange);
            }

            if (currentCanvasController) {
                await currentCanvasController.removeAsCurrentCanvas(false);
            }

            if (currentSelectionLayerController) {
                await currentSelectionLayerController.reset();
            }

            const canvasController = canvasControllers[slide.id];

            // in Video slide in order to show the preview we need to set it as current canvas
            // We do not want to set isSingleSlideEditor to true for video slides
            // because we want to be sure the user won't be able to modify the slide,
            // just see the preview
            const canEditSharedSlide = slide.get("template_id") === "video" || isSingleSlideEditor;

            await canvasController.setAsCurrentCanvas(canEditSharedSlide)
                .catch(err => {
                    logger.error(err, "[PresentationEditorController] error setting canvas as current, will keep in broken state", { slideId: slide.id });
                });

            let currentSlideLockOwner = null;
            if (this.collaborationSlidesLockService) {
                const lockState = this.collaborationSlidesLockService.getLockState(slide.id);
                if (lockState?.isLockedForMe) {
                    currentSlideLockOwner = lockState.lockedBy;
                }
            }

            await this._updateState({
                currentSlide: slide,
                currentCanvasController: canvasController,
                currentSlideLockOwner,
                currentSelectionLayerController: selectionLayerControllers[slide.id],
                freezePropertiesPanel: true,
                showSelectionLayer: this.canShowSelectionLayer(slide, canvasController, isSingleSlideEditor, slideNavMode)
            });

            const slideIndex = slide.getIndex();

            // Push the slide index to the url
            clearTimeout(this.pushStateTimeout);
            // Don't update URL when editor is in popup/blurred mode (e.g., BlurredEditor in onboarding)
            if (allowEditing && !isSingleSlideEditor && !presentation.get("isDummy") && !this._state.popupOpenState && window.history.pushState && !isTriggeredByPopState && !window.location.pathname.includes("/settings/") && !window.location.pathname.includes("/theme/")) {
                // Delaying the pushState call until user settles on a specific slide for smoother transition as current url
                // change triggers a shit ton of analytics scripts that make a lot of network calls on production
                this.pushStateTimeout = setTimeout(() => {
                    window.history.replaceState({}, "", `/${presentation.id}/${slideIndex + 1}`);
                }, 500);
            }

            // Legacy
            ds.selection.slide = slide;

            // Keep the comments panel open when navigating between slides - it takes precedence
            // over automatically revealing the Slide AI panel for not yet viewed/generating slides
            if ((slide.get("hasBeenViewed") === false || slide.getMetadata()?.isGenerating) && !this.isCommentsPanelActive) {
                this.setSelectedPropertyPanelTab(PanelType.SLIDE_AI, { width: 350 });
            }

            // Listen for changes to the slide
            slide.on("change", this._onCurrentSlideChange);

            this.unfreezePropertiesPanelTimeout = setTimeout(() => {
                this._updateState({ freezePropertiesPanel: false });
            }, 350);

            ux.addSlideToPresentation.complete({}, metadata => metadata.slideIds?.includes(slide.id));
        };

        if (!runSequentially) {
            return setCurrentSlide();
        }

        return new Promise((resolve, reject) => {
            this.asyncActionsPromiseChain = this.asyncActionsPromiseChain
                .then(setCurrentSlide)
                .then(resolve)
                .catch(reject);
        });
    }

    async setCurrentSlideByIndex(index, runSequentially = true, isTriggeredByPopState = false) {
        const { slides } = this._state;
        index = Math.clamp(index, 0, slides.length - 1);
        await this.setCurrentSlide(slides[index], runSequentially, isTriggeredByPopState);
    }

    async setCurrentSlideById(id) {
        const { slides } = this._state;
        const targetSlide = slides.find(slide => slide.id === id);
        if (!targetSlide) {
            throw new Error(`Slide with id ${id} not found`);
        }
        await this.setCurrentSlide(targetSlide);
    }

    async showAddSlideDialog() {
        const AddSlideContainer = await import(/* webpackMode: "eager" */ "js/react/views/AddSlide/AddSlideContainer").then(module => module.default);
        await this.currentCanvasController.removeAsCurrentCanvas(false);
        const result = await ShowDialogAsync(AddSlideContainer);
        await this.currentCanvasController.setAsCurrentCanvas();
        if (this._state.showPropertyPanel) {
            this.setSelectedPropertyPanelTab("element");
        }
        if (result) {
            this.showSlideAddedCompletionPopover();
        }
    }

    showSlideAddedCompletionPopover() {
        if (!QuickStartController.isInitialized) return;

        setTimeout(() => {
            QuickStartController.requestAddSlideCompletionPopover();
        }, 100);
    }

    async duplicateSlide() {
        const { presentation, currentSlide } = this._state;

        const insertIndex = this.getCurrentSlideIndex() + 1;

        const [newSlide] = await presentation.duplicateSlide(currentSlide, { insertIndex, isSlideCreatedFromUserAction: true });

        const { slideTemplates: { slideTemplates } } = await getCanvasBundle(newSlide.get("version") ?? appVersion);
        const duplicateSlideCount = 1;
        const eventProps = {
            "slide_ids": [newSlide.get("id")],
            "source_slides": [currentSlide.get("id")],
            "slide_template_names": [slideTemplates[currentSlide.get("template_id")].title],
            "slides_created": duplicateSlideCount,
        };
        trackActivity("Slide", "Duplicate", null, duplicateSlideCount, eventProps, { audit: true });
    }

    async deleteSlide() {
        const { presentation, currentSlide, currentCanvasController } = this._state;

        if (presentation.slides.length <= 1) {
            ShowWarningDialog({
                title: "Can't delete slide",
                message: "Sorry, you can't delete the only slide in a presentation"
            });
            return;
        }

        await this.hideCanvasControls();
        await this.hideSelectionLayer();

        const slideIndex = this.getCurrentSlideIndex();
        if (slideIndex === 0) {
            await this.setCurrentSlideByIndex(1);
        } else {
            await this.setCurrentSlideByIndex(slideIndex - 1);
        }

        await currentCanvasController.setOpacity(0);

        await new Promise(resolve => setTimeout(resolve, 300));

        await presentation.destroySlides(currentSlide.id);

        await this.showCanvasControls();
    }

    async renamePresentation() {
        const { presentation } = this._state;

        const presentationName = await ShowInputDialog({
            title: "Enter a name for your presentation",
            value: presentation.get("name"),
            trimInput: true,
            onChange: str => sanitizeHtmlText(str)
        });

        if (!presentationName) {
            return;
        }

        await presentation.rename(presentationName);
    }

    undo() {
        app.undoManager.undo();
    }

    redo() {
        app.undoManager.redo();
    }

    isSlideGridVisible() {
        return this._state.showSlideGrid;
    }

    toggleSlideGrid() {
        if (this._state.showSlideGrid) {
            return this.hideSlideGrid();
        }

        return this.showSlideGrid();
    }

    async showSlideGrid() {
        await this.currentCanvasController.removeAsCurrentCanvas(false);

        await this._updateState({
            showSlideGrid: true,
            showSelectionLayer: false,
            showCanvasControls: false
        });
    }

    async hideSlideGrid() {
        const { currentSlide, currentCanvasController, isSingleSlideEditor, slideNavMode } = this._state;

        await this.currentCanvasController.setAsCurrentCanvas();

        await this._updateState({
            showSlideGrid: false,
            showSelectionLayer: this.canShowSelectionLayer(currentSlide, currentCanvasController, isSingleSlideEditor, slideNavMode),
            showCanvasControls: true
        });
    }

    setPopupState(popupOpenState) {
        return this._updateState({
            popupOpenState
        });
    }

    setAllowEditing(allowEditing) {
        return this._updateState({ allowEditing });
    }

    showCanvasControls() {
        return this._updateState({ showCanvasControls: true });
    }

    hideCanvasControls() {
        return this._updateState({ showCanvasControls: false });
    }

    async showRightPanel(type) {
        const { currentCanvasController } = this._state;

        await this._updateState({
            activeRightPanel: type,
            activeBottomPanel: null
        });

        currentCanvasController.lockSlideForCollaborators();
    }

    async showBottomPanel(type) {
        const { currentCanvasController } = this._state;

        await this._updateState({
            activeBottomPanel: type,
            activeRightPanel: null
        });

        currentCanvasController.lockSlideForCollaborators();
    }

    async showElementPanel(forElement) {
        const { currentCanvasController } = this._state;

        await this._updateState({
            activeBottomPanel: PanelType.ELEMENT,
            ElementPanel: forElement.getElementPanel(),
            elementPanelElement: forElement
        });

        currentCanvasController.lockSlideForCollaborators();
    }

    async closeRightPanel() {
        const { currentCanvasController, currentSlide, isSingleSlideEditor, slideNavMode } = this._state;

        await this._updateState({
            activeRightPanel: null,
            showSelectionLayer: this.canShowSelectionLayer(currentSlide, currentCanvasController, isSingleSlideEditor, slideNavMode),
        });
    }

    async closeBottomPanel() {
        const { currentCanvasController, currentSlide, isSingleSlideEditor, slideNavMode } = this._state;

        await this._updateState({
            activeBottomPanel: null,
            ElementPanel: null,
            elementPanelElement: null,
            showSelectionLayer: this.canShowSelectionLayer(currentSlide, currentCanvasController, isSingleSlideEditor, slideNavMode),
        });
    }

    showEditorPopup(value) {
        return this._updateState({
            showEditorPopup: value,
        });
    }

    toggleComments() {
        if (this.isCommentsPanelActive) {
            return this.hidePropertyPanel();
        }

        this.setSelectedPropertyPanelTab(PanelType.COMMENTS, { goToSlide: index => this.setCurrentSlideByIndex(index) });
    }

    toggleAssignSlide() {
        const { activeRightPanel } = this._state;

        const isCommentsPanelActive = activeRightPanel === PanelType.ASSIGN_SLIDE;

        if (isCommentsPanelActive) {
            return this.closeRightPanel();
        }

        return this.showRightPanel(PanelType.ASSIGN_SLIDE);
    }

    get isNotesPanelActive() {
        const { activeBottomPanel } = this._state;
        return activeBottomPanel === PanelType.NOTES;
    }

    get selectedPropertyPanelTab() {
        return this._state.selectedPropertyPanelTab;
    }

    trackSlideNavigation = ({ method }) => {
        const presentation = this._state.presentation;
        const deviceType = app.isMobileOrTablet ? "mobile" : "desktop";

        return trackActivity("Editor", "Navigated", null, null, {
            presentation_id: presentation.id,
            method: method,
            deviceType: deviceType,
            ownerId: presentation.userId,
            userId: app.user.id,
            source: "editor",
            referrer: (document.referrer && !document.referrer.includes(location.host)) ? document.referrer : "direct",
        });
    }

    toggleNotes() {
        if (this.isNotesPanelActive) {
            return this.closeBottomPanel();
        }

        return this.showBottomPanel(PanelType.NOTES);
    }

    async showSelectionLayer() {
        const { currentCanvasController, currentSlide, isSingleSlideEditor, slideNavMode } = this._state;

        await this._updateState({
            showSelectionLayer: this.canShowSelectionLayer(currentSlide, currentCanvasController, isSingleSlideEditor, slideNavMode)
        });

        if (!currentCanvasController) {
            return;
        }

        currentCanvasController.setEditable(true);
    }

    async hideSelectionLayer() {
        const { currentCanvasController, currentSelectionLayerController } = this._state;

        if (currentSelectionLayerController) {
            await currentSelectionLayerController.setSelectedElements([]);
        }

        await this._updateState({
            showSelectionLayer: false
        });

        currentCanvasController.setEditable(false);
    }

    setSkipSlide(slide, value) {
        const { presentation } = this._state;
        presentation.skipSlide(slide, value);
    }

    batchSkipSlides(slidesModel, skip) {
        const { presentation } = this._state;
        if (skip) {
            presentation.batchSkipSlides(slidesModel);
        } else {
            presentation.batchUnskipSlides(slidesModel);
        }
    }

    async playAnimation() {
        await this.hideSelectionLayer();

        await this.getCurrentCanvasController().canvas.animate();

        await this.showSelectionLayer();
    }

    async exportPresentation() {
        const { default: ExportDialog } = await import(/* webpackMode: "eager" */ "js/react/views/PresentationSettings/dialogs/ExportDialog");
        await ShowDialogAsync(ExportDialog, { presentation: this._state.presentation });
    }

    async showThemeEditor() {
        const { presentation } = this._state;

        QuickStartController.markQuickStartTaskAsDone(QuickStartTask.PERSONALIZE_YOUR_THEME);

        const isTeamTheme = presentation.has("sharedThemeId");
        if (isTeamTheme && !WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage) {
            ShowWarningDialog({
                title: "Sorry, you don't have permission to edit this Team Theme.",
                message: "Team Themes are shared and only editable by team Owners and Librarians.",
                acceptButtonText: "Got it"
            });
            return;
        }

        if (isTeamTheme) {
            const sharedThemeId = presentation.get("sharedThemeId");
            const teamThemeUsageCount = await getPresentationsUsingSharedThemeCount({ workspaceId: WorkspaceController.workspaceId, themeId: sharedThemeId });

            const shouldContinue = await ShowConfirmationDialog({
                title: "Editing team theme",
                message: `Any changes you make will impact ${pluralize(
                    "presentation",
                    teamThemeUsageCount,
                    "presentations"
                )} in your workspace.`,
                cancelButtonLabel: "Cancel",
                okButtonLabel: "Continue editing",
                optOutProperty: "editTeamThemeDialog"
            });

            if (!shouldContinue) {
                return;
            }
        }

        AppController.showThemeEditor({ presentationId: presentation.id });
    }

    async showSwitchThemeDialog() {
        const onlyTeamThemes = !WorkspaceController.actionPermissions[ActionPermissionsObject.CUSTOM_THEMES].use;

        QuickStartController.markQuickStartTaskAsDone(QuickStartTask.PERSONALIZE_YOUR_THEME);
        this.selectionLayerController?.setSelectedElements([]);
        this.canvasController?.lockSlideForCollaborators(30);

        const { ChooseThemeDialog } = await import(/* webpackMode: "eager" */ "js/editor/ThemeEditor/ChooseThemeDialog");
        const selectedTheme = await ShowDialogAsync(ChooseThemeDialog, {
            title: "Switch Theme",
            hideUserThemes: onlyTeamThemes,
            hideBuiltInThemes: onlyTeamThemes,
            onlyTeamThemes,
            closeDialogOnThemeChoose: true
        });

        if (selectedTheme) {
            await withProgressDialog({
                title: "Switching theme...",
                action: async () => {
                    this.ignorePresentationThemeChanges = true;

                    await themeManager.switchTheme(selectedTheme, true);

                    await this.updateTheme(true);

                    this.ignorePresentationThemeChanges = false;
                }
            });
        }

        this.canvasController?.unlockSlideForCollaborators();
    }

    async updateTheme(resetSlideBackgrounds) {
        const { presentation, currentSelectionLayerController } = this._state;

        await currentSelectionLayerController?.setSelectedElements([]);

        this.loadSlidesMetadata(presentation);

        const theme = await themeManager.loadTheme(presentation);
        await this._updateState({ currentTheme: theme });
        app.currentTheme = theme;

        if (resetSlideBackgrounds) {
            for (let slide of presentation.slides) {
                if (slide.loaded && !slide.isLibrarySlide) {
                    slide.update({ layout: { ...slide.get("layout"), backgroundColor: null, backgroundStyle: null } });
                }
            }
        }

        const editorCanvases = this.getCanvases();

        const currentSlideId = this.currentSlide.id;
        const canvases = [
            ...editorCanvases.filter(canvas => canvas.slide.id === currentSlideId),
            ...editorCanvases.filter(canvas => canvas.slide.id !== currentSlideId),
        ];

        await withMaxConcurrency(canvases.map(canvas => async () => {
            await canvas.renderModel(true);
        }), 2);
    }

    handleShowSearchForText = async showFindAndReplaceBar => {
        if (showFindAndReplaceBar) {
            await this.setSelectedPropertyPanelTab("find_and_replace", {
                selectedPropertyPanelTab: this.selectedPropertyPanelTab
            });
        }
        await this._updateState({
            showFindAndReplaceBar,
            showPropertyPanel: this.selectedPropertyPanelTab ? true : showFindAndReplaceBar
        });
    }

    async getTextForAllSlides() {
        const canvasControllers = Object.values(this._state.canvasControllers);

        // Create an array of tasks, one for each canvas controller
        const tasks = canvasControllers.map(canvasController => async () => {
            const canvas = await canvasController.renderCanvas();

            // make sure to not include shared slide in the search
            if (canvas.slide.isLibrarySlide()) {
                return [];
            }

            const textBlocks = canvas
                .getStageElementsByType("TextElement")
                .filter(el => el.canEdit && el.blockContainerRef?.current?.blockRefs)
                .flatMap(el => el.blocks)
                .filter(Boolean)
                .filter(b => b.type === AuthoringBlockType.TEXT);

            const mappedTextBlocks = textBlocks.map(block => ({
                blockUid: block.uid,
                blockRef: block.ref.current,
                text: block.textContent,
                slideIndex: canvas.getSlideIndex(),
                elementPath: block.props.element.uniquePath
            }));

            // We can have more than one table in a slide
            const tableBlocks = canvas
                .getStageElementsByType("Table")
                .map(table => table.cells.map((cell, index) => ({
                    blockRef: cell.containerRef,
                    cellIndex: index,
                    text: cell.value,
                    slideIndex: canvas.getSlideIndex(),
                    elementPath: table.uniquePath
                })).filter(item => item.blockRef !== null && item.blockRef !== undefined)
                ).flat();

            return [...mappedTextBlocks, ...tableBlocks];
        });

        // Execute tasks with limited concurrency
        const results = await withMaxConcurrency(tasks);
        return results.flat();
    }

    async updateSlideVersion(slide, slideVersion = appVersion) {
        const confirmed = await ShowConfirmationDialog({
            title: "Hey, this Smart Slide got a bit smarter!",
            showRobot: true,
            optOutProperty: "updateSlideConfirmationDialog",
            message: (
                <>
                    <p>Occasionally, we make improvements or add new features to our Smart Slides.</p>
                    <p>Updating your slide is completely <strong>optional</strong> but if you'd like to take
                        advantage of the improvements on this existing slide, you can update it using the <strong>Update
                            Slide</strong> button.</p>
                    <blockquote>
                        <p><strong>NOTE</strong> In some cases, features, styles, or appearance may be different
                            from the previous version.</p>
                        <p>If you are unhappy with any changes, you can undo or revert to a previous version of your
                            slide using the version history.</p>
                    </blockquote>
                </>
            ),
            okButtonLabel: "Update Slide"
        });

        if (!confirmed) {
            return;
        }

        slide.commit({ undo: false }); // Preserve changes (if any)

        const canvasController = this.getCanvasControllerForSlide(slide);

        // Reload the canvas forcing to the current slide version
        await canvasController.reloadCanvas(slideVersion);

        // Save the migration immediately
        await canvasController.canvas.saveCanvasModel();

        await this.setCurrentSlide(slide);
    }

    async setSelectedPropertyPanelTab(selectedPropertyPanelTab, selectedPropertyPanelTabProps) {
        await this._updateState({
            selectedPropertyPanelTab,
            selectedPropertyPanelTabProps,
            showPropertyPanel: true,
        });
        this.setUIPrefs({ showPropertyPanel: true });
    }

    togglePropertyPanel() {
        this._updateState({ showPropertyPanel: !this._state.showPropertyPanel });
    }

    showPropertyPanel() {
        this._updateState({ showPropertyPanel: true });
    }

    hidePropertyPanel() {
        this._updateState({
            selectedPropertyPanelTab: null,
            showPropertyPanel: false,
        });
        this.setUIPrefs({ showPropertyPanel: false });
    }

    setRightPanelTransitionState(transitionState) {
        this._updateState({ rightPanelTransitionState: transitionState });
    }

    setSlideNavSelected(slideNavSelected) {
        this._updateState({ slideNavSelected });
    }

    showSlideNav() {
        this._updateState({ showSlideNav: true });
        this.setUIPrefs({ showSlideNav: true });
    }

    hideSlideNav() {
        this._updateState({ showSlideNav: false });
        this.setUIPrefs({ showSlideNav: false });
    }

    setSlideNavMode(mode) {
        this._updateState({ slideNavMode: mode });
    }

    setSlideNavWidth(slideNavWidth) {
        if (this._state.slideNavWidth === slideNavWidth) {
            return;
        }

        this._updateState({
            slideNavWidth
        });

        this.setUIPrefs({ slideNavWidth });
    }

    showOutlineView() {
        this._updateState({ showOutlineView: true });
    }

    hideOutlineView() {
        this._updateState({ showOutlineView: false });
    }

    setHideBottomControls(value) {
        this._updateState({ hideBottomControls: value });
    }

    toggleUITheme() {
        if (this._state.uiTheme == "light") {
            this._updateState({ uiTheme: "dark" });
            this.setUIPrefs({ theme: "dark" });
        } else {
            this._updateState({ uiTheme: "light" });
            this.setUIPrefs({ theme: "light" });
        }
    }
}

const presentationEditorController = new PresentationEditorController(_.cloneDeep(initialState));
export default presentationEditorController;

if (!app.mainView) {
    app.mainView = {};
}

app.mainView.editorView = {
    lockModel: {
        setLockState: () => {
        }
    },
    getContainerBounds: () => {
        return {
            canvasBounds: new geom.Rect(0, 0, 1280, 1656)
        };
    }
};

app.presentationEditorController = presentationEditorController;
